import { useEffect, useState } from 'react';
import { listarApuracoesIrpjCsll, apurarIrpjCsll, aprovarIrpjCsll, abrirDocumentoAutenticado } from '../api';
import { PageHeader, Loading, Empty, StatusBadge, Alert, KPI } from '../components/common';
import { fmtMoeda, fmtData, anosOpcoes } from '../lib/format';

const PRESUNCOES_IRPJ = [
  { value: 0.08,  label: '8% — Comércio / Indústria / Transporte de carga' },
  { value: 0.16,  label: '16% — Transporte (exceto carga) / Hospitalares / Serviços ≤R$120k/ano' },
  { value: 0.32,  label: '32% — Serviços em geral (consultoria, locação, intermediação)' },
  { value: 0.016, label: '1,6% — Revenda de combustível' },
];

const PRESUNCOES_CSLL = [
  { value: 0.12, label: '12% — Comércio / Indústria / Transporte' },
  { value: 0.32, label: '32% — Serviços em geral' },
];

const TRIMESTRES = [1, 2, 3, 4];

export default function ApuracaoIrpjCsll({ clienteId }) {
  const anoAtual = new Date().getFullYear();
  const triAtual = Math.ceil((new Date().getMonth() + 1) / 3);

  const [filtroAno, setFiltroAno] = useState(anoAtual);
  const [apuracoes, setApuracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState(null);
  const [formAberto, setFormAberto] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  // Form
  const [formAno, setFormAno] = useState(anoAtual);
  const [formTri, setFormTri] = useState(triAtual);
  const [formReceita, setFormReceita] = useState('');
  const [formPresuncaoIrpj, setFormPresuncaoIrpj] = useState(0.32);
  const [formPresuncaoCsll, setFormPresuncaoCsll] = useState(0.32);
  const [formAdicoes, setFormAdicoes] = useState('');
  const [formRetencaoIrpj, setFormRetencaoIrpj] = useState('');
  const [formRetencaoCsll, setFormRetencaoCsll] = useState('');
  const [apurando, setApurando] = useState(false);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    listarApuracoesIrpjCsll(clienteId, { ano: filtroAno })
      .then(r => setApuracoes(r.items || []))
      .catch(err => setMensagem({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };
  useEffect(carregar, [clienteId, filtroAno]);

  const apurar = async () => {
    setApurando(true);
    setMensagem(null);
    try {
      const dados = {
        trimestre: `${formAno}-T${formTri}`,
        presuncao_irpj: Number(formPresuncaoIrpj),
        presuncao_csll: Number(formPresuncaoCsll),
        adicoes_irpj: Number(formAdicoes || 0),
        adicoes_csll: Number(formAdicoes || 0),
        retencoes_irpj: Number(formRetencaoIrpj || 0),
        retencoes_csll: Number(formRetencaoCsll || 0),
      };
      if (formReceita) dados.receita_bruta = Number(formReceita);
      const r = await apurarIrpjCsll(clienteId, dados);
      const a = r.apuracao;
      setMensagem({
        tipo: 'success',
        txt: `${a.trimestre} apurado · IRPJ R$ ${a.irpj_a_pagar} + CSLL R$ ${a.csll_a_pagar}`
          + (r.receita ? ` · Receita ${fmtMoeda(r.receita.valor)} (${r.receita.origem})` : ''),
      });
      setFormReceita(''); setFormAdicoes('');
      setFormRetencaoIrpj(''); setFormRetencaoCsll('');
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    } finally {
      setApurando(false);
    }
  };

  const aprovar = async (id) => {
    try {
      await aprovarIrpjCsll(id);
      setMensagem({ tipo: 'success', txt: `Apuração #${id} aprovada` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  const totalIrpj  = apuracoes.reduce((s, a) => s + Number(a.irpj_a_pagar || 0), 0);
  const totalCsll  = apuracoes.reduce((s, a) => s + Number(a.csll_a_pagar || 0), 0);

  // Memoria de calculo imprimivel: rota autenticada, aba aberta no gesto.
  const imprimir = (a) =>
    abrirDocumentoAutenticado(
      `/apuracao-irpj-csll/${clienteId}/${a.trimestre}/html`, 'apuracao de IRPJ/CSLL')
      .catch(err => setMensagem({ tipo: 'danger', txt: 'Não foi possível gerar: ' + err.message }));

  return (
    <>
      <PageHeader
        title="Apuração IRPJ/CSLL"
        sub="Lucro Presumido — trimestral (Lei 9.430/96)"
        actions={
          <button className="primary" onClick={() => setFormAberto(v => !v)}>
            {formAberto ? '▴ Ocultar formulário' : '+ Nova apuração'}
          </button>
        }
      />

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      <div className="kpi-grid">
        <KPI label={`IRPJ total ${filtroAno}`} value={fmtMoeda(totalIrpj)} />
        <KPI label={`CSLL total ${filtroAno}`} value={fmtMoeda(totalCsll)} />
        <KPI label={`Trimestres em ${filtroAno}`} value={`${apuracoes.length}/4`} />
        <KPI label="Adicional IRPJ" value="10%" sub="Sobre excedente R$ 60k/tri" />
      </div>

      {formAberto && (
        <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '3px solid var(--gold)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
            Nova apuração trimestral
          </h3>
          <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div>
              <label className="small muted">Ano</label>
              <select value={formAno} onChange={e => setFormAno(Number(e.target.value))} style={{ width: 100 }}>
                {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="small muted">Trimestre</label>
              <select value={formTri} onChange={e => setFormTri(Number(e.target.value))} style={{ width: 120 }}>
                {TRIMESTRES.map(t => <option key={t} value={t}>T{t}</option>)}
              </select>
            </div>
            <div>
              <label className="small muted">Receita do trimestre (vazio = automático)</label>
              <input type="number" step="0.01" min="0" value={formReceita}
                     onChange={e => setFormReceita(e.target.value)}
                     placeholder="auto"
                     style={{ width: 220, textAlign: 'right' }} />
            </div>
          </div>

          <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 360 }}>
              <label className="small muted">Presunção IRPJ</label>
              <select value={formPresuncaoIrpj} onChange={e => setFormPresuncaoIrpj(Number(e.target.value))}>
                {PRESUNCOES_IRPJ.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 360 }}>
              <label className="small muted">Presunção CSLL</label>
              <select value={formPresuncaoCsll} onChange={e => setFormPresuncaoCsll(Number(e.target.value))}>
                {PRESUNCOES_CSLL.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label className="small muted">Adições (ganhos capital, rend. aplic.)</label>
              <input type="number" step="0.01" min="0" value={formAdicoes}
                     onChange={e => setFormAdicoes(e.target.value)}
                     style={{ width: 200, textAlign: 'right' }} placeholder="0,00" />
            </div>
            <div>
              <label className="small muted">Retenção IRPJ (R-4020)</label>
              <input type="number" step="0.01" min="0" value={formRetencaoIrpj}
                     onChange={e => setFormRetencaoIrpj(e.target.value)}
                     style={{ width: 160, textAlign: 'right' }} />
            </div>
            <div>
              <label className="small muted">Retenção CSLL (R-4020)</label>
              <input type="number" step="0.01" min="0" value={formRetencaoCsll}
                     onChange={e => setFormRetencaoCsll(e.target.value)}
                     style={{ width: 160, textAlign: 'right' }} />
            </div>
            <button className="primary" onClick={apurar} disabled={apurando}>
              {apurando ? 'Apurando…' : 'Apurar'}
            </button>
          </div>

          <p className="small muted" style={{ marginTop: 10 }}>
            Receita do trimestre é resolvida automaticamente (escriturado → NFS-e); preencha manualmente apenas como último recurso.
            IRPJ adicional: 10% sobre o que excede R$ 60k de base no trimestre.
            Saldo credor de trimestre anterior é abatido automaticamente.
          </p>
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading /> : apuracoes.length === 0 ? (
          <Empty title={`Sem apurações em ${filtroAno}`} />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Trimestre</th>
                <th className="num">Receita</th>
                <th>Pres. IRPJ</th>
                <th className="num">Base IRPJ</th>
                <th className="num">IRPJ a pagar</th>
                <th>Pres. CSLL</th>
                <th className="num">Base CSLL</th>
                <th className="num">CSLL a pagar</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {apuracoes.map(a => (
                <tr key={a.id}>
                  <td className="bold">{a.trimestre}</td>
                  <td className="num">{fmtMoeda(a.receita_bruta)}</td>
                  <td>{(Number(a.presuncao_irpj) * 100).toFixed(1)}%</td>
                  <td className="num">{fmtMoeda(a.base_irpj)}</td>
                  <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(a.irpj_a_pagar)}</td>
                  <td>{(Number(a.presuncao_csll) * 100).toFixed(1)}%</td>
                  <td className="num">{fmtMoeda(a.base_csll)}</td>
                  <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(a.csll_a_pagar)}</td>
                  <td className="small">{fmtData(a.darf_data_vencto)}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td className="flex">
                    <button className="ghost" onClick={() => setDetalhe(a)}>Ver</button>
                    <button className="ghost" onClick={() => imprimir(a)}
                      title="Memoria de calculo em A4, com assinaturas">&#128424;</button>
                    {a.status === 'rascunho' && (
                      <button className="primary" onClick={() => aprovar(a.id)}>Aprovar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && <DetalheIrpjCsll apuracao={detalhe} onClose={() => setDetalhe(null)} />}
    </>
  );
}

function DetalheIrpjCsll({ apuracao, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: 640, maxHeight: '85vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>IRPJ/CSLL {apuracao.trimestre}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose}>Fechar</button>
        </div>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 16, marginBottom: 8 }}>
          IRPJ (DARF {apuracao.irpj_darf_codigo})
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <tbody>
            <tr>
              <td>Receita bruta do trimestre{apuracao.receita_origem ? ` · origem ${apuracao.receita_origem}` : ''}</td>
              <td className="num bold">{fmtMoeda(apuracao.receita_bruta)}</td>
            </tr>
            <tr><td>Presunção IRPJ ({(Number(apuracao.presuncao_irpj) * 100).toFixed(1)}%)</td>
                <td className="num">{fmtMoeda(Number(apuracao.receita_bruta) * Number(apuracao.presuncao_irpj))}</td></tr>
            {Number(apuracao.adicoes_irpj) > 0 && (
              <tr><td>Adições</td><td className="num">+ {fmtMoeda(apuracao.adicoes_irpj)}</td></tr>
            )}
            {Number(apuracao.exclusoes_irpj) > 0 && (
              <tr><td>Exclusões</td><td className="num">− {fmtMoeda(apuracao.exclusoes_irpj)}</td></tr>
            )}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">Base IRPJ</td>
              <td className="num bold">{fmtMoeda(apuracao.base_irpj)}</td>
            </tr>
            <tr><td>IRPJ normal (15%)</td><td className="num">{fmtMoeda(apuracao.irpj_normal)}</td></tr>
            {Number(apuracao.irpj_adicional) > 0 && (
              <tr><td>Adicional (10% sobre excedente R$ 60k)</td>
                  <td className="num">{fmtMoeda(apuracao.irpj_adicional)}</td></tr>
            )}
            <tr><td>IRPJ apurado</td><td className="num">{fmtMoeda(apuracao.irpj_apurado)}</td></tr>
            <tr><td>Retenção R-4020</td><td className="num">({fmtMoeda(apuracao.irpj_retencao)})</td></tr>
            {Number(apuracao.irpj_saldo_credor) > 0 && (
              <tr><td>Saldo credor (próx. tri.)</td>
                  <td className="num" style={{ color: 'var(--success)' }}>{fmtMoeda(apuracao.irpj_saldo_credor)}</td></tr>
            )}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">IRPJ a pagar</td>
              <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(apuracao.irpj_a_pagar)}</td>
            </tr>
          </tbody>
        </table>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 16, marginBottom: 8 }}>
          CSLL (DARF {apuracao.csll_darf_codigo})
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <tbody>
            <tr><td>Presunção CSLL ({(Number(apuracao.presuncao_csll) * 100).toFixed(1)}%)</td>
                <td className="num">{fmtMoeda(Number(apuracao.receita_bruta) * Number(apuracao.presuncao_csll))}</td></tr>
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">Base CSLL</td>
              <td className="num bold">{fmtMoeda(apuracao.base_csll)}</td>
            </tr>
            <tr><td>CSLL apurada (9%)</td><td className="num">{fmtMoeda(apuracao.csll_apurada)}</td></tr>
            <tr><td>Retenção R-4020</td><td className="num">({fmtMoeda(apuracao.csll_retencao)})</td></tr>
            {Number(apuracao.csll_saldo_credor) > 0 && (
              <tr><td>Saldo credor (próx. tri.)</td>
                  <td className="num" style={{ color: 'var(--success)' }}>{fmtMoeda(apuracao.csll_saldo_credor)}</td></tr>
            )}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">CSLL a pagar</td>
              <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(apuracao.csll_a_pagar)}</td>
            </tr>
          </tbody>
        </table>

        <div className="flex" style={{ marginTop: 20, gap: 12, justifyContent: 'space-between' }}>
          <div className="small muted">
            Vencimento (último dia útil do mês seguinte ao trimestre) → <strong>{fmtData(apuracao.darf_data_vencto)}</strong>
          </div>
          <button className="primary" disabled title="Geração de DARF — próxima sprint">
            Gerar DARFs (em breve)
          </button>
        </div>
      </div>
    </div>
  );
}
